import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ADD_CUSTOMER_SECONDARY_PHONE_MUTATION,
  CUSTOMER_SECONDARY_PHONES_QUERY,
  REMOVE_CUSTOMER_SECONDARY_PHONE_MUTATION,
} from './customers.gql';

import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';

interface SecondaryPhone {
  id: string;
  phone: string;
  label: string | null;
}

interface Props {
  customer_id: string;
  can_write: boolean;
}

const ERROR_KEYS: Record<string, string> = {
  secondary_phone_invalid: 'customers.error_secondary_phone_invalid',
  secondary_phone_is_primary: 'customers.error_secondary_phone_is_primary',
  secondary_phone_already_primary: 'customers.error_secondary_phone_already_primary',
  secondary_phone_already_linked: 'customers.error_secondary_phone_already_linked',
};

export function CustomerSecondaryPhonesCard({ customer_id, can_write }: Props): JSX.Element {
  const { t } = useTranslation();
  const { data, refetch } = useQuery<{ customer: { secondary_phones: SecondaryPhone[] } }>(
    CUSTOMER_SECONDARY_PHONES_QUERY,
    { variables: { id: customer_id }, fetchPolicy: 'cache-and-network' },
  );
  const [add, { loading: adding }] = useMutation(ADD_CUSTOMER_SECONDARY_PHONE_MUTATION);
  const [remove, { loading: removing }] = useMutation(REMOVE_CUSTOMER_SECONDARY_PHONE_MUTATION);

  const [phone, setPhone] = useState('');
  const [label, setLabel] = useState('');
  const [error_text, setErrorText] = useState<string | null>(null);

  const phones = data?.customer.secondary_phones ?? [];

  const onClickAdd = async (): Promise<void> => {
    setErrorText(null);
    try {
      await add({
        variables: { input: { customer_id, phone: phone.trim(), label: label.trim() || null } },
      });
      setPhone('');
      setLabel('');
      await refetch();
    } catch (err) {
      if (err instanceof ApolloError) {
        const code = err.graphQLErrors[0]?.message ?? err.message;
        setErrorText(t(ERROR_KEYS[code] ?? code));
      } else if (err instanceof Error) {
        setErrorText(err.message);
      }
    }
  };

  const onClickRemove = async (id: string): Promise<void> => {
    setErrorText(null);
    try {
      await remove({ variables: { id } });
      await refetch();
    } catch (err) {
      if (err instanceof ApolloError) setErrorText(err.graphQLErrors[0]?.message ?? err.message);
      else if (err instanceof Error) setErrorText(err.message);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('customers.secondary_phones_title')}</CardTitle>
        <p className="text-xs text-muted-foreground">{t('customers.secondary_phones_subtitle')}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {error_text ? (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-sm text-destructive">
            {error_text}
          </div>
        ) : null}

        {phones.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('customers.no_secondary_phones')}</p>
        ) : (
          <ul className="space-y-2">
            {phones.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
              >
                <div>
                  <span className="font-mono">{p.phone}</span>
                  {p.label ? <span className="ml-2 text-muted-foreground">— {p.label}</span> : null}
                </div>
                {can_write ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={removing}
                    onClick={() => {
                      void onClickRemove(p.id);
                    }}
                  >
                    <X className="h-4 w-4" aria-hidden />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {can_write ? (
          <div className="flex flex-wrap items-end gap-2 pt-1">
            <div className="w-40 space-y-1">
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t('customers.phone')}
                inputMode="tel"
                disabled={adding}
              />
            </div>
            <div className="w-40 space-y-1">
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={t('customers.secondary_phone_label_placeholder')}
                disabled={adding}
              />
            </div>
            <Button
              type="button"
              size="sm"
              disabled={adding || phone.trim().length < 7}
              onClick={() => {
                void onClickAdd();
              }}
            >
              <Plus className="h-4 w-4" aria-hidden />
              <span>{t('customers.add_secondary_phone')}</span>
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
